import { useState, type FormEvent } from "react";

export type SubmitStatus = "idle" | "submitting" | "success" | "error";

interface UseFormSubmitResult {
  status: SubmitStatus;
  errorMessage: string;
  submit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  reset: () => void;
}

interface ParsedFormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

/**
 * Reserved anti-spam field names. If any of these is filled in, the submission is
 * treated as a bot: we show the normal success feedback without sending anything.
 */
const HONEYPOT_FIELDS = ["website_alt", "company_alt", "phone_alt", "mobile_alt", "contact_alt"];

/**
 * Shared handler for the platform form storage endpoints.
 * Submits as application/x-www-form-urlencoded and only reports success when the
 * HTTP status is 2xx AND the parsed payload code is "OK".
 */
export function useFormSubmit(endpoint: string): UseFormSubmitResult {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const reset = () => {
    setStatus("idle");
    setErrorMessage("");
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypotHit = HONEYPOT_FIELDS.some((field) => {
      const value = formData.get(field);
      return typeof value === "string" && value.trim().length > 0;
    });

    // Silently treat bot submissions as a normal success — never send them anywhere.
    if (honeypotHit) {
      setStatus("success");
      setErrorMessage("");
      form.reset();
      return;
    }

    HONEYPOT_FIELDS.forEach((field) => formData.delete(field));

    setStatus("submitting");
    setErrorMessage("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string") {
          body.append(key, value);
        }
      });

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const responseText = await response.text();
      let parsed: ParsedFormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedFormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        setErrorMessage("");
        form.reset();
      } else {
        setStatus("error");
        setErrorMessage(
          String(serverMsg).trim() || "Something went wrong. Please try again in a moment.",
        );
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "We could not reach the server. Please check your connection and try again.",
      );
    }
  };

  return { status, errorMessage, submit, reset };
}